import React, { FunctionComponent, useEffect } from "react"
import { Redirect } from "react-router-dom"
import { useSelector } from "react-redux"

import { getSSOLoginURL, getUseSSO } from "../../../data/api"
import { ApplicationState } from "../../../core/types"
import { User } from "../../../data/model/user-model"
import { beforeSSOLogin } from "../components/LoginForm"

/**
 * Single routing decision made at app load. Replaces the old
 * `/` -> `/home` -> `/instruments` redirect chain and the `LoginForm`
 * SSO auto-redirect, both of which produced a visible flicker.
 *
 * - Authenticated (cached session): one direct hop to the home page.
 * - Not authenticated + SSO enabled: send the browser straight to the IdP.
 * - Not authenticated + no SSO: render the local login page.
 */
export const AuthRedirect: FunctionComponent = () => {
    const refreshToken = useSelector<ApplicationState, string | null>( state => state.auth.refresh_token )
    const user = useSelector<ApplicationState, User | null>( state => state.auth.user )
    const isNewUIEnabled = useSelector<ApplicationState, boolean>( state => state.dev.isNewUIEnabled )

    const isAuthenticated = !!(refreshToken && user)
    const ssoLoginUrl = (!isAuthenticated && getUseSSO()) ? getSSOLoginURL() : ""

    useEffect(() => {
        // Full-page navigation to the IdP. Kept in an effect (not during render)
        // so the redirect fires as a side effect after commit.
        if (ssoLoginUrl) {
            beforeSSOLogin()
            window.location.replace(ssoLoginUrl)
        }
    }, [ssoLoginUrl])

    if (isAuthenticated) {
        return <Redirect to={isNewUIEnabled ? "/newhome" : "/instruments"} />
    }

    if (ssoLoginUrl) {
        // SSO redirect is happening in the effect above; nothing to render.
        return null
    }

    return <Redirect to="/login" />
}
